import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/utils/supabase';
import type { Product } from '@/utils/supabase';
import { getCacheBuster } from './refreshStore';

interface ProductState {
  products: Product[];
  featuredProducts: Product[];
  categories: string[];
  isLoading: boolean;
  error: string | null;
  totalProducts: number;
  currentPage: number;
  hasMore: boolean;
  lastFetchedAt: number;
  lastFeaturedFetchedAt: number;
  fetchProducts: (options?: {
    isAdmin?: boolean;
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    isFeatured?: boolean;
    append?: boolean;
    sort?: string;
  }) => Promise<void>;
  fetchFeaturedProducts: () => Promise<void>;
  fetchProductById: (id: string) => Promise<Product | null>;
  fetchProductsByCategory: (category: string) => Promise<Product[]>;
  searchProducts: (query: string) => Product[];
  filterProducts: (filters: {
    category?: string;
    minPrice?: number;
    maxPrice?: number;
    inStock?: boolean;
  }) => Product[];

  // Admin actions
  addProduct: (product: Omit<Product, 'id' | 'created_at' | 'updated_at' | 'rating' | 'reviews_count'>, signal?: AbortSignal) => Promise<Product | null>;
  updateProduct: (id: string, updates: Partial<Product>, signal?: AbortSignal) => Promise<Product | null>;
  deleteProduct: (id: string, signal?: AbortSignal) => Promise<boolean>;
  uploadImage: (file: File) => Promise<string | null>;
  initRealtimeSubscription: () => void;
  cleanupRealtimeSubscription: () => void;
}

export const useProductStore = create<ProductState>()(
  persist(
    (set, get) => ({
  products: [],
  featuredProducts: [],
  categories: ['dark_chocolate', 'milk_chocolate', 'white_chocolate', 'truffles', 'brownies', 'cakes', 'gift_boxes', 'assorted'],
  isLoading: false,
  error: null,
  totalProducts: 0,
  currentPage: 1,
  hasMore: false,
  lastFetchedAt: 0,
  lastFeaturedFetchedAt: 0,

  fetchProducts: async (options = {}) => {
    const {
      isAdmin = false,
      page = 1,
      limit = 20,
      search = '',
      category = 'all',
      isFeatured,
      append = false,
      sort = 'newest'
    } = options;

    const queryLimit = Math.min(limit, 100);
    const now = Date.now();
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

    // If it's a default initial load (no special filters/search) and we have fresh cache, skip DB
    const isDefaultQuery = !append && !search && category === 'all' && isFeatured === undefined && sort === 'newest' && page === 1;
    if (isDefaultQuery && get().products.length > 0 && (now - get().lastFetchedAt) < CACHE_TTL && !isAdmin) {
      return; // Use cached products
    }

    // Only show loading spinner on initial load, not background refreshes
    if (get().products.length === 0) set({ isLoading: true });
    set({ error: null });
    try {
      let query = supabase
        .from('products')
        .select('*', { count: 'exact' })
        .neq('id', getCacheBuster());

      if (!isAdmin) {
        query = query.eq('is_active', true);
      }

      if (category && category !== 'all') {
        query = query.eq('category', category);
      }

      if (isFeatured !== undefined) {
         query = query.eq('is_featured', isFeatured);
      }

      if (search) {
        const sanitizedSearch = search.replace(/[\\%_]/g, '\\$&');
        query = query.or(`name_en.ilike.%${sanitizedSearch}%,name_bn.ilike.%${sanitizedSearch}%,description_en.ilike.%${sanitizedSearch}%,description_bn.ilike.%${sanitizedSearch}%`);
      }

      // Apply sorting
      switch (sort) {
        case 'price_low':
          query = query.order('price', { ascending: true });
          break;
        case 'price_high':
          query = query.order('price', { ascending: false });
          break;
        case 'name':
          query = query.order('name_en', { ascending: true });
          break;
        case 'newest':
        default:
          query = query.order('created_at', { ascending: false });
      }

      // Apply pagination
      const from = (page - 1) * queryLimit;
      const to = from + queryLimit - 1;
      query = query.range(from, to);

      const { data: result, error: fetchError, count } = await query;

      if (fetchError) throw fetchError;
      
      const newProducts = result || [];
      const currentProducts = get().products;
      
      let allProducts = newProducts;
      if (append) {
        const existingIds = new Set(currentProducts.map(p => p.id));
        const uniqueNewProducts = newProducts.filter(p => !existingIds.has(p.id));
        allProducts = [...currentProducts, ...uniqueNewProducts];
      }
      
      const total = count || 0;
      const hasMore = allProducts.length < total;

      set({ 
        products: allProducts, 
        totalProducts: total,
        currentPage: page,
        hasMore,
        isLoading: false,
        lastFetchedAt: isDefaultQuery ? now : get().lastFetchedAt
      });
    } catch (error: unknown) {
      if ((error instanceof Error && error.name === 'AbortError') || (error && typeof error === 'object' && 'message' in error && String(error.message).includes('AbortError'))) {
        return;
      }
      
      console.error('CRITICAL: Fetch products error:', error);
      const message = error instanceof Error ? error.message : String(error);
      if (import.meta.env.DEV) console.error(message); set({ error: 'An unexpected error occurred.', isLoading: false });
    }
  },

  fetchFeaturedProducts: async () => {
    const now = Date.now();
    const CACHE_TTL = 5 * 60 * 1000;
    
    if (get().featuredProducts.length > 0 && (now - get().lastFeaturedFetchedAt) < CACHE_TTL) {
       return;
    }

    if (get().featuredProducts.length === 0) set({ isLoading: true });
    set({ error: null });
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .neq('id', getCacheBuster())
        .eq('is_active', true)
        .eq('is_featured', true)
        .limit(6);

      if (error) throw error;
      set({ featuredProducts: data || [], isLoading: false, lastFeaturedFetchedAt: now });
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error('Fetch featured products error:', error);
      const message = error instanceof Error ? error.message : String(error)
      if (import.meta.env.DEV) console.error(message); set({ error: 'An unexpected error occurred.', isLoading: false });
    }
  },

  fetchProductById: async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return data;
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error('Error fetching product:', error);
      return null;
    }
  },

  fetchProductsByCategory: async (category: string) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('category', category)
        .eq('is_active', true);

      if (error) throw error;
      return data || [];
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error('Error fetching products by category:', error);
      return [];
    }
  },

  searchProducts: (query: string) => {
    const { products } = get();
    const lowerQuery = query.toLowerCase();

    return products.filter(
      (product) =>
        product.name_en.toLowerCase().includes(lowerQuery) ||
        product.name_bn.includes(query) ||
        (product.description_en?.toLowerCase().includes(lowerQuery) ?? false) ||
        (product.description_bn?.includes(query) ?? false)
    );
  },

  filterProducts: (filters) => {
    const { products } = get();

    return products.filter((product) => {
      if (filters.category && product.category !== filters.category) {
        return false;
      }
      if (filters.minPrice !== undefined && product.price < filters.minPrice) {
        return false;
      }
      if (filters.maxPrice !== undefined && product.price > filters.maxPrice) {
        return false;
      }
      if (filters.inStock && product.stock_quantity <= 0) {
        return false;
      }
      return true;
    });
  },

  addProduct: async (productData, signal) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('products')
        .insert([productData])
        .select();
        
      if (signal) {
        query = query.abortSignal(signal);
      }

      const { data, error } = await query.single();

      if (error) throw error;

      const { products } = get();
      set({ products: [data, ...products], isLoading: false });
      return data;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      if (import.meta.env.DEV) console.error(message); set({ error: 'An unexpected error occurred.', isLoading: false });
      return null;
    }
  },

  updateProduct: async (id, updates, signal) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('products')
        .update(updates)
        .eq('id', id)
        .select();
        
      if (signal) {
        query = query.abortSignal(signal);
      }

      const { data, error } = await query.single();

      if (error) throw error;

      const { products, featuredProducts } = get();
      const updatedProducts = products.map(p => p.id === id ? data : p);
      const updatedFeatured = featuredProducts.map(p => p.id === id ? data : p);

      set({
        products: updatedProducts,
        featuredProducts: updatedFeatured,
        isLoading: false
      });
      return data;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      if (import.meta.env.DEV) console.error(message); set({ error: 'An unexpected error occurred.', isLoading: false });
      return null;
    }
  },

  deleteProduct: async (id, signal) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('products')
        .delete()
        .eq('id', id);
        
      if (signal) {
        query = query.abortSignal(signal);
      }
        
      const { error } = await query;

      if (error) throw error;

      const { products, featuredProducts } = get();
      set({
        products: products.filter(p => p.id !== id),
        featuredProducts: featuredProducts.filter(p => p.id !== id),
        isLoading: false
      });
      return true;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      if (import.meta.env.DEV) console.error(message); set({ error: 'An unexpected error occurred.', isLoading: false });
      return false;
    }
  },

  uploadImage: async (file) => {
    set({ isLoading: true, error: null });
    
    if (!file.type.startsWith('image/')) {
      set({ error: 'Invalid file type. Only images are allowed.', isLoading: false });
      return null;
    }

    // Compression Helper
    const compressImage = (file: File): Promise<Blob> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target?.result as string;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            const maxDim = 800; // Optimal for web/mobile

            if (width > height) {
              if (width > maxDim) {
                height *= maxDim / width;
                width = maxDim;
              }
            } else {
              if (height > maxDim) {
                width *= maxDim / height;
                height = maxDim;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
              if (blob) resolve(blob);
              else reject(new Error('Canvas toBlob failed'));
            }, 'image/jpeg', 0.85); // High quality but compressed
          };
          img.onerror = (e) => reject(e);
        };
        reader.onerror = (e) => reject(e);
      });
    };

    try {
      const compressedBlob = await compressImage(file);
      const fileExt = 'jpg'; // Normalizing to jpg
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, compressedBlob, {
          contentType: 'image/jpeg',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      set({ isLoading: false });
      return data.publicUrl;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      if (import.meta.env.DEV) console.error(message); set({ error: 'An unexpected error occurred.', isLoading: false });
      return null;
    }
  },

  initRealtimeSubscription: async () => {
    const channels = supabase.getChannels();
    const existingChannel = channels.find(c => c.topic.includes('product_updates'));

    // [INDUSTRIAL GRADE RE-SYNC]
    // Strictly kill any previous connection before starting a new one. 
    // This prevents the 'Multiple Channels' error that causes browsers to hang.
    if (existingChannel) {
        if (import.meta.env.DEV) console.log('[Realtime-Products] ️ Resetting stale connection pool...');
        await supabase.removeChannel(existingChannel);
    }

    let retryCount = 0;
    const MAX_RETRIES = 5;

    const connect = () => {
        supabase.channel('product_updates')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, (payload) => {
            const { eventType, new: newRecord, old: oldRecord } = payload;
            const updatedProduct = newRecord as Product;

            if (eventType === 'UPDATE') {
              set((state) => ({
                 products: state.products.map(p => p.id === updatedProduct.id ? { ...p, ...updatedProduct } : p),
                 featuredProducts: state.featuredProducts.map(p => p.id === updatedProduct.id ? { ...p, ...updatedProduct } : p)
              }));
            } else if (eventType === 'DELETE') {
              set((state) => ({
                 products: state.products.filter(p => p.id !== oldRecord.id),
                 featuredProducts: state.featuredProducts.filter(p => p.id !== oldRecord.id)
              }));
            }
          })
          .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    if (import.meta.env.DEV) console.log('%c[Realtime-Products] 🟢 ACTIVE', 'color: #3b82f6; font-weight: bold;');
                    retryCount = 0;
                } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
                    console.error(`[Realtime-Products] 🔴 OFFLINE: ${status}`);
                    
                    if (retryCount < MAX_RETRIES) {
                        retryCount++;
                        // Fast 1s reconnection on 'Strong' fix
                        setTimeout(() => connect(), 1000);
                    }
                }
            });
    };

    connect();
  },

  cleanupRealtimeSubscription: () => {
    const channels = supabase.getChannels();
    const productChannel = channels.find(c => c.topic.includes('product_updates'));
    if (productChannel) supabase.removeChannel(productChannel);
  }
}), {
  name: 'product-cache',
  partialize: (state) => ({ 
    products: state.products, 
    featuredProducts: state.featuredProducts,
    lastFetchedAt: state.lastFetchedAt,
    lastFeaturedFetchedAt: state.lastFeaturedFetchedAt
  })
}));

